import { useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  Badge,
  Modal,
  ModalFooter,
  LoadingSpinner,
  ErrorSummary,
  EmptyState,
  ContentLoader,
} from '@/components/common';
import { useBulkSelection, BulkActionBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '@/components/bulk';
import { applyServerErrors } from '@/utils/formErrors';
import {
  useCities,
  useCreateCity,
  useUpdateCity,
  useDeleteCity,
  useToggleCityActive,
  useCountryLookup,
  useStateLookup,
  useCity,
} from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { City, CityRequest } from '@/services/geography';

const tierOptions = [
  { value: '', label: 'All Tiers' },
  { value: 'metro', label: 'Metro' },
  { value: 'tier1', label: 'Tier 1' },
  { value: 'tier2', label: 'Tier 2' },
  { value: 'tier3', label: 'Tier 3' },
  { value: 'rural', label: 'Rural' },
];

const tierColors: Record<string, 'primary' | 'success' | 'warning' | 'info' | 'error'> = {
  metro: 'primary',
  tier1: 'success',
  tier2: 'info',
  tier3: 'warning',
  rural: 'error',
};

export function CitiesPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  // Item 19: clicking a row opens a detail popup with the city's state and
  // its other fields (lat/long, tier, pincode/RTO counts) that the list
  // columns don't have room for.
  const [viewCityId, setViewCityId] = useState<string | null>(null);

  const { data: countriesData } = useCountryLookup();
  const { data: statesData } = useStateLookup(countryFilter || undefined);
  const { data, isLoading, isError, error, refetch } = useCities(page, {
    search: debouncedSearch,
    country: countryFilter || undefined,
    state: stateFilter || undefined,
    tier: tierFilter || undefined,
  });
  const createMutation = useCreateCity();
  const updateMutation = useUpdateCity();
  const deleteMutation = useDeleteCity();
  const toggleActiveMutation = useToggleCityActive();

  // Item 19: multi-select + bulk status change/delete via the Wave 0 kit.
  const visibleIds = (data?.results || []).map((city) => city.id);
  const bulkSelection = useBulkSelection(visibleIds);

  const [formData, setFormData] = useState<CityRequest>({
    state: '',
    name: '',
    code: '',
    tier: 'tier2',
    is_active: true,
  });

  const [formCountry, setFormCountry] = useState('');
  const { data: formStatesData } = useStateLookup(formCountry || undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryMessages, setSummaryMessages] = useState<string[]>([]);

  const countryOptions = [
    { value: '', label: 'All Countries' },
    ...(countriesData?.map(c => ({ value: c.id, label: c.name })) || []),
  ];

  const stateOptions = [
    { value: '', label: 'All States' },
    ...(statesData?.map(s => ({ value: s.id, label: s.name })) || []),
  ];

  const handleOpenModal = (city?: City) => {
    if (city) {
      setEditingCity(city);
      setFormData({
        state: city.state,
        name: city.name,
        code: city.code || '',
        tier: city.tier,
        latitude: city.latitude,
        longitude: city.longitude,
        is_active: city.is_active,
      });
    } else {
      setEditingCity(null);
      setFormCountry(countryFilter);
      setFormData({
        state: stateFilter || '',
        name: '',
        code: '',
        tier: 'tier2',
        is_active: true,
      });
    }
    setFieldErrors({});
    setSummaryMessages([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setSummaryMessages([]);
    try {
      if (editingCity) {
        await updateMutation.mutateAsync({ id: editingCity.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setIsModalOpen(false);
    } catch (error) {
      const nextErrors: Record<string, string> = {};
      const messages = applyServerErrors(error, (name, err) => {
        nextErrors[name as string] = err.message;
      });
      setFieldErrors(nextErrors);
      setSummaryMessages(messages);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const columns = [
    {
      key: 'select',
      header: '',
      // See the "select all" strip above the table for the header checkbox --
      // Table's column header is plain text, so it can't live in the <th>.
      render: (city: City) => (
        <div onClick={(e) => e.stopPropagation()}>
          <BulkSelectCheckbox
            checked={bulkSelection.isSelected(city.id)}
            onChange={() => bulkSelection.toggle(city.id)}
            label={`Select ${city.name}`}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'City',
      render: (city: City) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{city.name}</p>
          {city.code && <p className="text-sm text-gray-500 dark:text-gray-400">Code: {city.code}</p>}
        </div>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (city: City) => city.state_name,
    },
    {
      key: 'country',
      header: 'Country',
      render: (city: City) => city.country_name,
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (city: City) => (
        <Badge variant={tierColors[city.tier]}>
          {city.tier.charAt(0).toUpperCase() + city.tier.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'pincodes_count',
      header: 'Pincodes',
      render: (city: City) => city.pincodes_count || 0,
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (city: City) => (
        <Badge variant={city.is_active ? 'success' : 'error'}>
          {city.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (city: City) => (
        // The row itself now opens the detail popup on click; these buttons
        // must not also trigger that.
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => toggleActiveMutation.mutate(city.id)}
            className={`px-2 py-1 text-xs rounded ${
              city.is_active
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {city.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleOpenModal(city)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(city.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    // A failed request must never render as "no cities" -- see StatesPage's
    // identical guard for the same reasoning.
    return (
      <div className="space-y-6">
        <PageHeader title="Cities" subtitle="Manage cities and districts" />
        <Card>
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="Could not load cities"
            description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Try again
              </button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cities"
        subtitle="Manage cities and districts"
        actions={
          <Button onClick={() => handleOpenModal()}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add City
          </Button>
        }
      />

      <Card>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search cities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              />
            </div>
            <div className="w-full sm:w-40">
              <Select
                options={countryOptions}
                value={countryFilter}
                onChange={(e) => {
                  setCountryFilter(e.target.value);
                  setStateFilter('');
                }}
              />
            </div>
            <div className="w-full sm:w-40">
              <Select
                options={stateOptions}
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                disabled={!countryFilter}
              />
            </div>
            <div className="w-full sm:w-32">
              <Select
                options={tierOptions}
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {visibleIds.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
            <BulkSelectAllCheckbox
              checked={bulkSelection.allVisibleSelected}
              indeterminate={bulkSelection.someVisibleSelected}
              onChange={bulkSelection.toggleAllVisible}
              label="Select all cities on this page"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">Select all on this page</span>
          </div>
        )}

        {bulkSelection.count > 0 && (
          <div className="px-4 pt-3">
            <BulkActionBar
              resource="geography/cities"
              selectedIds={bulkSelection.selectedIds}
              entityName="cities"
              onChanged={() => refetch()}
              onClear={bulkSelection.clear}
            />
          </div>
        )}

        <Table
          columns={columns}
          data={data?.results || []}
          emptyMessage="No cities found"
          rowKey={(city) => city.id}
          onRowClick={(city) => setViewCityId(city.id)}
        />

        {data && data.count > 20 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.next}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCity ? 'Edit City' : 'Add City'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorSummary messages={summaryMessages} />
          <Select
            label="Country"
            options={countriesData?.map(c => ({ value: c.id, label: c.name })) || []}
            value={formCountry}
            onChange={(e) => {
              setFormCountry(e.target.value);
              setFormData({ ...formData, state: '' });
            }}
            error={fieldErrors.country}
            required
          />
          <Select
            label="State"
            options={formStatesData?.map(s => ({ value: s.id, label: s.name })) || []}
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            disabled={!formCountry}
            error={fieldErrors.state}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={fieldErrors.name}
              required
            />
            <Input
              label="City Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              error={fieldErrors.code}
            />
          </div>
          <Select
            label="Tier"
            options={[
              { value: 'metro', label: 'Metro' },
              { value: 'tier1', label: 'Tier 1' },
              { value: 'tier2', label: 'Tier 2' },
              { value: 'tier3', label: 'Tier 3' },
              { value: 'rural', label: 'Rural' },
            ]}
            value={formData.tier}
            onChange={(e) => setFormData({ ...formData, tier: e.target.value as City['tier'] })}
            error={fieldErrors.tier}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Latitude"
              type="number"
              step="0.000001"
              value={formData.latitude || ''}
              onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || undefined })}
              error={fieldErrors.latitude}
            />
            <Input
              label="Longitude"
              type="number"
              step="0.000001"
              value={formData.longitude || ''}
              onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || undefined })}
              error={fieldErrors.longitude}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
          {fieldErrors.is_active && (
            <p className="text-sm text-red-600">{fieldErrors.is_active}</p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingCity ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete City"
      >
        <p className="text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this city? This will also affect associated pincodes and RTOs.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Item 19: clicking a city opens its details -- its State and the
          fields the list columns have no room for (lat/long, RTO count). */}
      <CityDetailModal cityId={viewCityId} onClose={() => setViewCityId(null)} />
    </div>
  );
}

/**
 * Item 19's detail popup. Unlike States→Cities (item 18) there is no child
 * list to page through here -- "its other details" means the record's own
 * fields -- so this fetches the single full detail row (`useCity`, which
 * already carries `pincodes_count`/`rtos_count`/lat-long that the list
 * serializer leaves out) rather than a paginated collection.
 */
function CityDetailModal({ cityId, onClose }: { cityId: string | null; onClose: () => void }) {
  const { data: city, isLoading, isError, error, refetch } = useCity(cityId ?? '');

  if (!cityId) return null;

  return (
    <Modal isOpen onClose={onClose} title={city?.name ?? 'City'} size="lg">
      {isLoading ? (
        <ContentLoader />
      ) : isError ? (
        <EmptyState
          icon={<ExclamationTriangleIcon />}
          title="Could not load this city"
          description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Try again
            </button>
          }
        />
      ) : !city ? null : (
        <div className="space-y-5">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">State</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.state_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Country</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.country_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Code</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.code || '-'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Tier</dt>
              <dd className="mt-1">
                <Badge variant={tierColors[city.tier]}>
                  {city.tier.charAt(0).toUpperCase() + city.tier.slice(1)}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Latitude</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.latitude ?? '-'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Longitude</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.longitude ?? '-'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Status</dt>
              <dd className="mt-1">
                <Badge variant={city.is_active ? 'success' : 'error'}>
                  {city.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Pincodes</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.pincodes_count ?? 0}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">RTOs</dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{city.rtos_count ?? 0}</dd>
            </div>
          </dl>

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
